/**
 * ImageModelManager Component
 *
 * Full-screen modal for managing local image checkpoint models for the two
 * local image providers: Fooocus (SDXL) and ComfyUI SD 1.5.
 *
 * - Lists installed and available curated models (GET /api/v1/image/models).
 * - Lets the user install a curated model on demand, search CivitAI for more
 *   models, and delete installed checkpoints.
 * - Reuses the same backend download/verify pipeline as the Deforum picker
 *   (POST/DELETE /api/v1/components/image-models/*), only differing by provider.
 *
 * Mirrors the look & feel of OllamaModelManager (dark glass theme, 44px touch
 * targets, mobile-first).
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  X,
  Download,
  Trash2,
  HardDrive,
  Search,
  CheckCircle,
  AlertCircle,
  Loader2,
  Box,
} from 'lucide-react';
import { useTranslation } from '../../hooks/useTranslation';
import { imageGenerationAPI } from '../../services/imageGenerationAPI';
import type { ImageModelInfo } from '../../types';

interface ImageModelManagerProps {
  isOpen: boolean;
  onClose: () => void;
  /** Backend image provider id: 'fooocus' or 'comfyui-sd'. */
  provider: string;
  /** Human-readable component name shown in the header (e.g. "Fooocus Image AI"). */
  title: string;
}

interface InstallState {
  status: 'downloading' | 'verifying' | 'installed' | 'failed' | 'queued';
  progress_mb: number;
  total_mb: number;
  error: string | null;
}

const POLL_INTERVAL_MS = 1500;
const SEARCH_DEBOUNCE_MS = 350;

/** Stable key for a model used for progress tracking. */
function modelKey(m: ImageModelInfo): string {
  return m.filename || m.id;
}

/** Resolve the install identifier for a model, or null if it cannot be installed. */
function installArgs(
  provider: string,
  m: ImageModelInfo
): {
  provider: string;
  slug?: string;
  civitai_version_id?: number;
  civitai_model_id?: number;
  hf_repo?: string;
  hf_filename?: string;
} | null {
  if (m.slug) return { provider, slug: m.slug };
  // Prefer the pinned version: installing by model id alone resolves to the
  // latest version, which is the wrong build for Lightning/distilled models.
  if (m.civitai_version_id) return { provider, civitai_version_id: m.civitai_version_id };
  // HF-hosted build (CivitAI-delisted): download from HuggingFace instead.
  if (m.hf_repo && m.hf_filename)
    return { provider, hf_repo: m.hf_repo, hf_filename: m.hf_filename };
  if (m.civitai_id) return { provider, civitai_model_id: m.civitai_id };
  return null;
}

export function ImageModelManager({ isOpen, onClose, provider, title }: ImageModelManagerProps) {
  const { t } = useTranslation();

  const [models, setModels] = useState<ImageModelInfo[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [query, setQuery] = useState('');
  const [searchResults, setSearchResults] = useState<ImageModelInfo[]>([]);
  const [searching, setSearching] = useState(false);
  const searchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [installState, setInstallState] = useState<Record<string, InstallState>>({});
  const [deleting, setDeleting] = useState<Record<string, boolean>>({});
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
  const pollTimers = useRef<Record<string, ReturnType<typeof setInterval>>>({});

  const refreshModels = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    const res = await imageGenerationAPI.getModels(provider);
    if (res.success && res.data) {
      setModels(Array.isArray(res.data.models) ? res.data.models : []);
    } else {
      setLoadError(res.error || t('imageModelManager.loadError', 'Failed to load models'));
    }
    setLoading(false);
  }, [provider, t]);

  // Load the model list whenever the modal opens.
  useEffect(() => {
    if (isOpen) void refreshModels();
  }, [isOpen, refreshModels]);

  // Clear all polling timers on unmount / close.
  useEffect(() => {
    const timers = pollTimers.current;
    return () => {
      Object.values(timers).forEach((id) => clearInterval(id));
    };
  }, []);

  // Debounced CivitAI search.
  useEffect(() => {
    if (!isOpen) return;
    if (searchTimer.current) clearTimeout(searchTimer.current);
    if (!query.trim()) {
      setSearchResults([]);
      setSearching(false);
      return;
    }
    setSearching(true);
    searchTimer.current = setTimeout(async () => {
      const res = await imageGenerationAPI.searchModels(query.trim(), 20);
      if (res.success && res.data) {
        setSearchResults(Array.isArray(res.data.models) ? res.data.models : []);
      } else {
        setSearchResults([]);
      }
      setSearching(false);
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      if (searchTimer.current) clearTimeout(searchTimer.current);
    };
  }, [query, isOpen]);

  const pollInstall = useCallback(
    (key: string, jobId: string) => {
      if (pollTimers.current[key]) clearInterval(pollTimers.current[key]);
      pollTimers.current[key] = setInterval(async () => {
        const res = await imageGenerationAPI.getImageModelInstallStatus(jobId);
        if (!res.success || !res.data) return;
        const data = res.data;
        setInstallState((prev) => ({ ...prev, [key]: data }));
        if (data.status === 'installed' || data.status === 'failed') {
          clearInterval(pollTimers.current[key]);
          delete pollTimers.current[key];
          if (data.status === 'installed') {
            void refreshModels();
          }
        }
      }, POLL_INTERVAL_MS);
    },
    [refreshModels]
  );

  const handleInstall = useCallback(
    async (m: ImageModelInfo) => {
      const args = installArgs(provider, m);
      if (!args) return;
      const key = modelKey(m);
      setInstallState((prev) => ({
        ...prev,
        [key]: { status: 'queued', progress_mb: 0, total_mb: m.size_mb || 0, error: null },
      }));
      const res = await imageGenerationAPI.installImageModel(args);
      if (res.success && res.data?.job_id) {
        pollInstall(key, res.data.job_id);
      } else {
        setInstallState((prev) => ({
          ...prev,
          [key]: {
            status: 'failed',
            progress_mb: 0,
            total_mb: 0,
            error: res.error || t('imageModelManager.installError', 'Install failed'),
          },
        }));
      }
    },
    [provider, pollInstall, t]
  );

  const handleDelete = useCallback(
    async (m: ImageModelInfo) => {
      setConfirmDelete(null);
      const key = modelKey(m);
      if (!m.filename) return;
      setDeleting((prev) => ({ ...prev, [key]: true }));
      const res = await imageGenerationAPI.deleteImageModel(provider, m.filename);
      setDeleting((prev) => ({ ...prev, [key]: false }));
      if (res.success) {
        void refreshModels();
      } else {
        setLoadError(res.error || t('imageModelManager.deleteError', 'Failed to delete model'));
      }
    },
    [provider, refreshModels, t]
  );

  if (!isOpen) return null;

  const installed = models.filter((m) => m.is_installed);
  const available = models.filter((m) => !m.is_installed);

  const renderProgress = (key: string) => {
    const st = installState[key];
    if (!st) return null;
    if (st.status === 'failed') {
      return (
        <div className="mt-2 flex items-center gap-2 text-xs text-red-400">
          <AlertCircle className="w-3.5 h-3.5" />
          <span>{st.error || t('imageModelManager.installError', 'Install failed')}</span>
        </div>
      );
    }
    if (st.status === 'installed') {
      return (
        <div className="mt-2 flex items-center gap-2 text-xs text-green-400">
          <CheckCircle className="w-3.5 h-3.5" />
          <span>{t('imageModelManager.installComplete', 'Installed')}</span>
        </div>
      );
    }
    const pct = st.total_mb > 0 ? Math.round((st.progress_mb / st.total_mb) * 100) : 0;
    return (
      <div className="mt-2">
        <div className="w-full bg-black/30 rounded-full h-2 overflow-hidden">
          <div
            className="bg-purple-500 h-2 rounded-full transition-all duration-300"
            style={{ width: `${Math.min(100, Math.max(0, pct))}%` }}
          />
        </div>
        <div className="flex items-center gap-1 text-xs text-white/50 mt-1">
          <Loader2 className="w-3 h-3 animate-spin" />
          {st.total_mb > 0
            ? `${st.progress_mb} / ${st.total_mb} MB`
            : t('common.loading', 'Loading...')}
        </div>
      </div>
    );
  };

  const renderRow = (m: ImageModelInfo, isInstalled: boolean) => {
    const key = modelKey(m);
    const st = installState[key];
    const isBusy =
      st?.status === 'queued' || st?.status === 'downloading' || st?.status === 'verifying';
    const canInstall = installArgs(provider, m) !== null;

    return (
      <div
        key={key}
        className="p-3 rounded-lg border border-white/10 bg-black/20 hover:bg-black/30"
      >
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            {m.image_url ? (
              <img
                src={m.image_url}
                alt=""
                className="w-10 h-10 rounded object-cover flex-shrink-0"
              />
            ) : (
              <div className="w-10 h-10 rounded bg-black/30 flex items-center justify-center flex-shrink-0">
                <Box className="w-5 h-5 text-white/30" />
              </div>
            )}
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-medium text-white/90 text-sm truncate">{m.name}</span>
                {m.recommended && (
                  <span className="text-[10px] uppercase tracking-wide text-purple-300 bg-purple-900/30 px-1.5 py-0.5 rounded">
                    {t('imageModelManager.recommended', 'Recommended')}
                  </span>
                )}
              </div>
              {m.description && (
                <p className="text-xs text-white/50 mt-0.5 truncate">{m.description}</p>
              )}
              <div className="flex flex-wrap gap-3 mt-1 text-xs text-white/40">
                <span>{m.base}</span>
                {m.size_mb ? (
                  <span className="flex items-center gap-1">
                    <HardDrive className="w-3 h-3" />
                    {(m.size_mb / 1024).toFixed(1)} GB
                  </span>
                ) : null}
                {m.creator && <span>by {m.creator}</span>}
              </div>
            </div>
          </div>

          <div className="flex-shrink-0">
            {isInstalled ? (
              confirmDelete === key ? (
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => void handleDelete(m)}
                    className="px-2 py-1 text-xs bg-red-600 text-white rounded hover:bg-red-700 min-h-[32px]"
                  >
                    {t('common.confirm', 'Confirm')}
                  </button>
                  <button
                    onClick={() => setConfirmDelete(null)}
                    className="px-2 py-1 text-xs text-white/60 hover:text-white/90 min-h-[32px]"
                  >
                    {t('common.cancel', 'Cancel')}
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => setConfirmDelete(key)}
                  disabled={deleting[key]}
                  className="p-2 text-white/40 hover:text-red-400 hover:bg-red-900/20 rounded-lg min-h-[44px] min-w-[44px] flex items-center justify-center"
                  title={t('imageModelManager.delete', 'Delete model')}
                >
                  {deleting[key] ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Trash2 className="w-4 h-4" />
                  )}
                </button>
              )
            ) : (
              <button
                onClick={() => void handleInstall(m)}
                disabled={isBusy || !canInstall}
                className={`p-2 rounded-lg min-h-[44px] min-w-[44px] flex items-center justify-center ${
                  canInstall && !isBusy
                    ? 'text-white/60 hover:text-purple-400 hover:bg-purple-900/20'
                    : 'text-white/20 cursor-not-allowed'
                }`}
                title={
                  canInstall
                    ? t('imageModelManager.install', 'Download')
                    : t('imageModelManager.notInstallable', 'No download source for this model')
                }
              >
                {isBusy ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Download className="w-4 h-4" />
                )}
              </button>
            )}
          </div>
        </div>
        {renderProgress(key)}
      </div>
    );
  };

  return (
    <div
      className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div
        className="bg-secondary-900 border border-white/10 rounded-xl w-full max-w-2xl max-h-[85vh] flex flex-col shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 sm:p-5 border-b border-white/10 flex-shrink-0">
          <div>
            <h2 className="text-lg font-semibold text-white/90">
              {t('imageModelManager.title', 'Manage Models')}
            </h2>
            <p className="text-xs text-white/40 mt-0.5">{title}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-white/40 hover:text-white/80 hover:bg-white/5 rounded-lg min-h-[44px] min-w-[44px] flex items-center justify-center"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Search bar */}
        <div className="px-4 sm:px-5 pt-4 flex-shrink-0">
          <div className="relative">
            <Search className="w-4 h-4 text-white/40 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t(
                'imageModelManager.searchPlaceholder',
                'Search CivitAI for more models...'
              )}
              className="w-full pl-9 pr-3 py-2.5 min-h-[44px] rounded-lg border border-white/10 bg-black/20 text-white text-sm placeholder:text-white/30 focus:border-primary-500 focus:ring-2 focus:ring-primary-500/30 outline-none"
            />
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-5 space-y-6">
          {loadError && (
            <div className="p-3 rounded-lg bg-red-900/20 border border-red-500/30 text-sm text-red-300">
              {loadError}
            </div>
          )}

          {/* Search results */}
          {query.trim() && (
            <section>
              <h3 className="text-sm font-semibold text-white/70 uppercase tracking-wider mb-3">
                {t('imageModelManager.searchResults', 'CivitAI Results')}
              </h3>
              {searching ? (
                <div className="flex items-center justify-center py-6">
                  <Loader2 className="w-5 h-5 text-white/40 animate-spin" />
                </div>
              ) : searchResults.length === 0 ? (
                <p className="text-sm text-white/40 py-2">
                  {t('imageModelManager.noResults', 'No models found.')}
                </p>
              ) : (
                <div className="space-y-2">{searchResults.map((m) => renderRow(m, false))}</div>
              )}
            </section>
          )}

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 text-white/40 animate-spin" />
            </div>
          ) : (
            <>
              {/* Installed */}
              <section>
                <h3 className="text-sm font-semibold text-white/70 uppercase tracking-wider mb-3">
                  {t('imageModelManager.installedModels', 'Installed Models')}
                  <span className="ml-2 text-white/30 font-normal normal-case">
                    ({installed.length})
                  </span>
                </h3>
                {installed.length === 0 ? (
                  <div className="p-4 rounded-lg border border-white/5 bg-black/10 text-center text-sm text-white/40">
                    {t('imageModelManager.noInstalled', 'No models installed yet.')}
                  </div>
                ) : (
                  <div className="space-y-2">{installed.map((m) => renderRow(m, true))}</div>
                )}
              </section>

              {/* Available curated */}
              <section>
                <h3 className="text-sm font-semibold text-white/70 uppercase tracking-wider mb-3">
                  {t('imageModelManager.availableModels', 'Available Models')}
                  <span className="ml-2 text-white/30 font-normal normal-case">
                    ({available.length})
                  </span>
                </h3>
                {available.length === 0 ? (
                  <div className="p-4 rounded-lg border border-white/5 bg-black/10 text-center text-sm text-white/40">
                    {t('imageModelManager.allInstalled', 'All curated models are installed.')}
                  </div>
                ) : (
                  <div className="space-y-2">{available.map((m) => renderRow(m, false))}</div>
                )}
              </section>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default ImageModelManager;
